import { Chip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { AVAILABILITY, ENGINE_TYPES, STATUSES } from '../constants';
import { availabilityColors, statusColors } from '../colors';
import type { AvailabilityKey, DashboardFilterKey, EngineTypeKey, PickupKey, Site, StatusKey } from '../types';
import { FILTER_LABELS as LABELS } from '../../../utils/labels';

const ALL_AVAILABILITY_KEYS = Object.keys(AVAILABILITY) as AvailabilityKey[];
const ALL_STATUS_KEYS = Object.keys(STATUSES) as StatusKey[];
const ALL_ENGINE_TYPE_KEYS = Object.keys(ENGINE_TYPES) as EngineTypeKey[];

function ActiveFilterChip({
  label,
  dotColor,
  onRemove,
}: {
  label: string;
  dotColor?: string;
  onRemove: () => void;
}) {
  return (
    <Chip
      icon={
        dotColor ? (
          <span
            className="block w-1.5 h-1.5 rounded-full ml-2 mr-0"
            style={{ backgroundColor: dotColor }}
          />
        ) : undefined
      }
      label={
        <span className="flex items-center gap-0.5 text-sm">
          {label}
          <CloseIcon fontSize="inherit" aria-hidden="true" />
        </span>
      }
      onClick={onRemove}
      aria-label={`${label} verwijderen`}
      variant="outlined"
    />
  );
}

interface ActiveFilterChipsBarProps {
  availabilityFilter: Set<AvailabilityKey>;
  toggleAvailability: (k: AvailabilityKey) => void;
  statusFilter: Set<StatusKey>;
  toggleStatus: (k: StatusKey) => void;
  engineTypeFilter: Set<EngineTypeKey>;
  toggleEngineType: (k: EngineTypeKey) => void;
  siteFilter: Set<string>;
  toggleSite: (id: string) => void;
  sites: Site[];
  supplierFilter: Set<string>;
  toggleSupplier: (id: string) => void;
  suppliers: { id: string; name: string }[];
  subcontractorFilter: Set<string>;
  toggleSubcontractor: (id: string) => void;
  subcontractors: { id: string; name: string }[];
  categoryFilter: Set<string>;
  toggleCategory: (category: string) => void;
  categories: string[];
  optionsFilter: Set<string>;
  toggleOption: (key: string) => void;
  options: { key: string; label: string }[];
  pickupFilter: Set<PickupKey>;
  togglePickup: (k: PickupKey) => void;
  dashboardFilter?: DashboardFilterKey | null;
  className?: string;
  pickupHandinEnabled?: boolean;
}

export default function ActiveFilterChipsBar({
  availabilityFilter,
  toggleAvailability,
  statusFilter,
  toggleStatus,
  engineTypeFilter,
  toggleEngineType,
  siteFilter,
  toggleSite,
  sites,
  supplierFilter,
  toggleSupplier,
  suppliers,
  subcontractorFilter,
  toggleSubcontractor,
  subcontractors,
  categoryFilter,
  toggleCategory,
  categories,
  optionsFilter,
  toggleOption,
  options,
  pickupFilter,
  togglePickup,
  dashboardFilter,
  className,
  pickupHandinEnabled = false,
}: ActiveFilterChipsBarProps) {
  const availabilityKeys = pickupHandinEnabled ? ALL_AVAILABILITY_KEYS : ALL_AVAILABILITY_KEYS.filter((k) => k !== 'reserved');
  const isAllAvailability = availabilityKeys.every((k) => availabilityFilter.has(k));
  const isAllStatus = ALL_STATUS_KEYS.every((k) => statusFilter.has(k));
  const isAllEngineType = ALL_ENGINE_TYPE_KEYS.every((k) => engineTypeFilter.has(k));
  const isAllSites = siteFilter.size === 0 || sites.every((s) => siteFilter.has(s.id));
  const isAllSuppliers =
    supplierFilter.size === 0 || suppliers.every((s) => supplierFilter.has(s.id));
  const isAllSubcontractors =
    subcontractorFilter.size === 0 || subcontractors.every((s) => subcontractorFilter.has(s.id));
  const isAllCategories =
    categoryFilter.size === 0 || categories.every((c) => categoryFilter.has(c));
  const isAllOptions = optionsFilter.size === 0 || options.every((o) => optionsFilter.has(o.key));
  const isAllPickup = (['confirmed', 'not_confirmed'] as PickupKey[]).every((k) =>
    pickupFilter.has(k)
  );
  const hasActiveFilters =
    !isAllAvailability || !isAllStatus || !isAllEngineType || !isAllSites || !isAllSuppliers || !isAllSubcontractors || !isAllCategories || !isAllOptions || !isAllPickup;

  if (dashboardFilter || !hasActiveFilters) return null;

  return (
    <div
      className={`border-b border-border flex flex-wrap gap-1.5 flex-shrink-0 px-3 py-1.5 ${className ?? ''}`}
    >
      {!isAllAvailability &&
        (Object.entries(AVAILABILITY) as [AvailabilityKey, { label: string }][])
          .filter(([k]) => availabilityFilter.has(k))
          .map(([k, v]) => (
            <ActiveFilterChip
              key={k}
              label={v.label}
              dotColor={availabilityColors[k]}
              onRemove={() => toggleAvailability(k)}
            />
          ))}
      {!isAllStatus &&
        (Object.entries(STATUSES) as [StatusKey, { label: string }][])
          .filter(([k]) => statusFilter.has(k))
          .map(([k, v]) => (
            <ActiveFilterChip
              key={k}
              label={v.label}
              dotColor={statusColors[k].dot}
              onRemove={() => toggleStatus(k)}
            />
          ))}
      {!isAllEngineType &&
        (Object.entries(ENGINE_TYPES) as [EngineTypeKey, { label: string }][])
          .filter(([k]) => engineTypeFilter.has(k))
          .map(([k, v]) => (
            <ActiveFilterChip
              key={k}
              label={v.label}
              onRemove={() => toggleEngineType(k)}
            />
          ))}
      {!isAllSites &&
        sites
          .filter((s) => siteFilter.has(s.id))
          .map((site) => (
            <ActiveFilterChip
              key={site.id}
              label={site.name}
              dotColor={site.color}
              onRemove={() => toggleSite(site.id)}
            />
          ))}
      {!isAllSuppliers &&
        suppliers
          .filter((s) => supplierFilter.has(s.id))
          .map((supplier) => (
            <ActiveFilterChip
              key={supplier.id}
              label={supplier.name}
              onRemove={() => toggleSupplier(supplier.id)}
            />
          ))}
      {!isAllSubcontractors &&
        subcontractors
          .filter((s) => subcontractorFilter.has(s.id))
          .map((subcontractor) => (
            <ActiveFilterChip
              key={subcontractor.id}
              label={subcontractor.name}
              onRemove={() => toggleSubcontractor(subcontractor.id)}
            />
          ))}
      {!isAllCategories &&
        categories
          .filter((c) => categoryFilter.has(c))
          .map((category) => (
            <ActiveFilterChip
              key={category}
              label={category}
              onRemove={() => toggleCategory(category)}
            />
          ))}
      {!isAllOptions &&
        options
          .filter((o) => optionsFilter.has(o.key))
          .map((option) => (
            <ActiveFilterChip
              key={option.key}
              label={option.label}
              onRemove={() => toggleOption(option.key)}
            />
          ))}
      {!isAllPickup && pickupFilter.has('confirmed') && !pickupFilter.has('not_confirmed') && (
        <ActiveFilterChip
          label={LABELS.pickupConfirmed}
          onRemove={() => togglePickup('confirmed')}
        />
      )}
      {!isAllPickup && pickupFilter.has('not_confirmed') && !pickupFilter.has('confirmed') && (
        <ActiveFilterChip
          label={LABELS.pickupNotConfirmed}
          onRemove={() => togglePickup('not_confirmed')}
        />
      )}
    </div>
  );
}
